import { wagmiConfig } from '@shared/configs/wagmi-config';
import {
  BSC_CHAIN_ID,
  ChainID,
  ETHEREUM_CHAIN_ID,
  RONIN_CHAIN_ID,
  SAIGON_CHAIN_ID,
} from '@shared/constants/chains';
import { Address, erc20Abi, maxInt256 } from 'viem';
import { readContract, waitForTransactionReceipt, writeContract } from 'wagmi/actions';

interface PowerPaymentDetails {
  contractAddress: string
  order: {
    orderId: string
    validUntil: number
    price: string
  }
}

const CHAIN_TOKEN_ADDRESS_MAP: Record<ChainID, Address> = {
  [RONIN_CHAIN_ID]: import.meta.env.VITE_RONIN_TOKEN_ADDRESS,
  [ETHEREUM_CHAIN_ID]:   import.meta.env.VITE_ETHEREUM_TOKEN_ADDRESS,
  [BSC_CHAIN_ID]: import.meta.env.VITE_BNB_TOKEN_ADDRESS,
  [SAIGON_CHAIN_ID]: import.meta.env.VITE_SAIGON_TESTNET_TOKEN_ADDRESS,
};

export const getTokenAddressByChainId = (chainId: ChainID): Address => {
  const tokenAddress = CHAIN_TOKEN_ADDRESS_MAP[chainId];
  if (!tokenAddress) {
    throw new Error(`Unsupported chainId: ${chainId}. Supported chains: ${Object.keys(CHAIN_TOKEN_ADDRESS_MAP).join(', ')}`);
  }

  return tokenAddress;
};

const readErc20AbiContract = async (
  account: `0x${string}` | undefined,
  data: PowerPaymentDetails,
  chainId: ChainID,
) => {
  try {
    return await readContract(wagmiConfig, {
      abi: erc20Abi,
      address: CHAIN_TOKEN_ADDRESS_MAP[chainId],
      functionName: 'allowance',
      args: [account as `0x${string}`, data?.contractAddress as `0x${string}`],
    });
  } catch (error) {
    throw new Error(error as string);
  }
};

const waitForTranReceipt = async (txHash: `0x${string}`) => {
  const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
    hash: txHash,
  });
  if (transactionReceipt.status !== 'success') {
    throw new Error('Transaction failed');
  }

  return transactionReceipt;
};

export const getErc20AbiApproval = async (
  data: PowerPaymentDetails,
  account: `0x${string}` | undefined,
  chainId: ChainID,
) => {
  try {
    const tokenAddress = getTokenAddressByChainId(chainId);
    const approvedAmount = await readErc20AbiContract(account, data, chainId);

    if (approvedAmount >= BigInt(data?.order?.price)) {
      return true;
    }

    const txHash = await writeContract(wagmiConfig, {
      abi: erc20Abi,
      address: tokenAddress,
      functionName: 'approve',
      args: [data?.contractAddress as `0x${string}`, maxInt256],
    });
    await waitForTranReceipt(txHash);

    return (
      (await readErc20AbiContract(account, data, chainId)) >= BigInt(data?.order?.price)
    );
  } catch (error) {
    throw new Error(error as string);
  }
};
